import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { RefreshCw, Play, Trash2, RotateCcw, Wifi, WifiOff, Clock, CheckCircle, XCircle, AlertTriangle, BarChart3 } from 'lucide-react';
import WebhookAnalyticsChart from './WebhookAnalyticsChart';

const API = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Pending' },
  retrying: { color: 'bg-orange-100 text-orange-800', icon: RefreshCw, label: 'Retrying' },
  delivered: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Delivered' },
  dead: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Dead Letter' },
};

export default function WebhookQueueMonitor() {
  const { getAuthHeaders } = useAuth();
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [view, setView] = useState('queue');

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/webhooks/queue/stats`, { headers: getAuthHeaders() });
      if (res.ok) setStats(await res.json());
    } catch (e) { console.error('Failed to fetch webhook stats', e); }
  }, [getAuthHeaders]);

  const fetchQueue = useCallback(async () => {
    try {
      const url = filter ? `${API}/api/webhooks/queue?status=${filter}` : `${API}/api/webhooks/queue`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setItems(data.items || []);
      }
    } catch (e) { console.error('Failed to fetch webhook queue', e); }
  }, [getAuthHeaders, filter]);

  const refresh = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchStats(), fetchQueue()]);
    setLoading(false);
  }, [fetchStats, fetchQueue]);

  useEffect(() => { refresh(); }, [refresh]);

  const triggerProcess = async () => {
    setProcessing(true);
    try {
      await fetch(`${API}/api/webhooks/queue/process-now`, {
        method: 'POST', headers: getAuthHeaders()
      });
      await refresh();
    } catch (e) { console.error(e); }
    setProcessing(false);
  };

  const retryWebhook = async (queueId) => {
    await fetch(`${API}/api/webhooks/queue/${queueId}/retry`, {
      method: 'POST', headers: getAuthHeaders()
    });
    await refresh();
  };

  const deleteWebhook = async (queueId) => {
    await fetch(`${API}/api/webhooks/queue/${queueId}`, {
      method: 'DELETE', headers: getAuthHeaders()
    });
    await refresh();
  };

  if (loading) return <div className="text-center p-8 text-gray-500">Loading webhook queue...</div>;

  return (
    <div className="space-y-6" data-testid="webhook-queue-monitor">
      {/* View Toggle */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setView('queue')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${view === 'queue' ? 'bg-blue-600 text-white shadow-md' : 'glass text-gray-600 hover:bg-gray-100'}`}
          data-testid="view-queue-btn"
        >
          <RefreshCw className="w-4 h-4" /> Queue
        </button>
        <button
          onClick={() => setView('analytics')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${view === 'analytics' ? 'bg-indigo-600 text-white shadow-md' : 'glass text-gray-600 hover:bg-gray-100'}`}
          data-testid="view-analytics-btn"
        >
          <BarChart3 className="w-4 h-4" /> Analytics
        </button>
      </div>

      {view === 'analytics' ? (
        <WebhookAnalyticsChart />
      ) : (
      <>
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Total', value: stats?.total || 0, icon: Wifi, color: 'text-blue-600 bg-blue-100' },
          { label: 'Pending', value: stats?.pending || 0, icon: Clock, color: 'text-yellow-600 bg-yellow-100' },
          { label: 'Retrying', value: stats?.retrying || 0, icon: RefreshCw, color: 'text-orange-600 bg-orange-100' },
          { label: 'Delivered', value: stats?.delivered || 0, icon: CheckCircle, color: 'text-green-600 bg-green-100' },
          { label: 'Dead Letter', value: stats?.dead || 0, icon: XCircle, color: 'text-red-600 bg-red-100' },
        ].map(s => (
          <Card key={s.label} className="glass hover-lift transition-all" data-testid={`webhook-stat-${s.label.toLowerCase().replace(' ', '-')}`}>
            <CardContent className="p-3 flex items-center gap-3">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${s.color}`}>
                <s.icon className="w-4 h-4" />
              </div>
              <div>
                <p className="text-xs text-gray-500">{s.label}</p>
                <p className="text-lg font-bold">{s.value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1 glass rounded-xl p-1">
          {['', 'pending', 'retrying', 'delivered', 'dead'].map(f => (
            <button
              key={f || 'all'}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                filter === f ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'
              }`}
              data-testid={`filter-${f || 'all'}`}
            >
              {f ? f.charAt(0).toUpperCase() + f.slice(1) : 'All'}
            </button>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={refresh} data-testid="refresh-queue-btn">
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
        <Button size="sm" onClick={triggerProcess} disabled={processing} data-testid="process-now-btn">
          <Play className="w-4 h-4 mr-1" /> {processing ? 'Processing...' : 'Process Now'}
        </Button>
        <div className="ml-auto flex items-center gap-2 text-sm text-gray-500">
          {stats?.processor_running ? (
            <><Wifi className="w-4 h-4 text-green-500" /> Queue processor active</>
          ) : (
            <><WifiOff className="w-4 h-4 text-red-500" /> Processor stopped</>
          )}
        </div>
      </div>

      {/* Backoff Schedule */}
      {stats?.config && (
        <Card className="glass">
          <CardContent className="p-3">
            <p className="text-xs font-medium text-gray-500 mb-1">Retry Schedule (exponential backoff)</p>
            <div className="flex flex-wrap gap-2">
              {stats.config.backoff_schedule.map((delay, i) => (
                <span key={i} className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                  Retry {i + 1}: {delay}
                </span>
              ))}
              <span className="px-2 py-0.5 bg-red-50 text-red-700 rounded-full text-xs font-medium">
                Then: Dead Letter
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Delivery Rate */}
      {stats && (
        <div className="flex items-center gap-2 text-sm">
          <AlertTriangle className="w-4 h-4 text-gray-400" />
          <span className="text-gray-600">Delivery rate: <strong>{stats.delivery_rate}</strong></span>
        </div>
      )}

      {/* Queue Items Table */}
      <Card className="glass">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Queue Items ({items.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <p className="text-center text-gray-400 py-6">No webhooks in queue</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-gray-500">
                    <th className="pb-2 pr-3">ID</th>
                    <th className="pb-2 pr-3">Vendor</th>
                    <th className="pb-2 pr-3">Event</th>
                    <th className="pb-2 pr-3">Status</th>
                    <th className="pb-2 pr-3">Attempts</th>
                    <th className="pb-2 pr-3">Last Error</th>
                    <th className="pb-2 pr-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => {
                    const cfg = statusConfig[item.status] || statusConfig.pending;
                    const Icon = cfg.icon;
                    return (
                      <tr key={item.queue_id} className="border-b border-gray-100 hover:bg-gray-50/50" data-testid={`queue-item-${item.queue_id}`}>
                        <td className="py-2 pr-3 font-mono text-xs">{item.queue_id}</td>
                        <td className="py-2 pr-3 text-xs">{item.vendor_id?.slice(0, 12)}...</td>
                        <td className="py-2 pr-3 text-xs">{item.event_type}</td>
                        <td className="py-2 pr-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
                            <Icon className="w-3 h-3" /> {cfg.label}
                          </span>
                        </td>
                        <td className="py-2 pr-3 text-xs text-center">{item.attempts}/{item.max_retries}</td>
                        <td className="py-2 pr-3 text-xs text-red-600 max-w-[200px] truncate">{item.last_error || '-'}</td>
                        <td className="py-2 pr-3">
                          <div className="flex gap-1">
                            {(item.status === 'dead' || item.status === 'retrying') && (
                              <button onClick={() => retryWebhook(item.queue_id)} className="p-1 rounded hover:bg-blue-100 text-blue-600" title="Retry" data-testid={`retry-${item.queue_id}`}>
                                <RotateCcw className="w-3.5 h-3.5" />
                              </button>
                            )}
                            <button onClick={() => deleteWebhook(item.queue_id)} className="p-1 rounded hover:bg-red-100 text-red-600" title="Delete" data-testid={`delete-${item.queue_id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      </>
      )}
    </div>
  );
}
